import React from 'react'
import Box from '@mui/material/Box'
import { adminColors, adminRadii, adminShadows } from '../theme/adminTokens'

// 긴 필터 화면에서 반복되는 폭/간격 기준을 여기서 통일한다.
export const LIST_FILTER_DIVIDER_SX = { height: 22, my: 0, mx: 0.5 }
// 날짜 필드는 label, YYYY-MM-DD 값, 달력 아이콘을 고려해 고정 폭으로 맞춘다.
export const LIST_FILTER_DATE_FIELD_SX = { width: 164 }
// 일반 select 필드는 목록 필터에서 가장 많이 쓰는 기본 폭이다.
export const LIST_FILTER_SELECT_FIELD_SX = { minWidth: 148 }
// 라벨이 긴 select는 기본 폭보다 조금 넓게 둔다.
export const LIST_FILTER_WIDE_SELECT_FIELD_SX = { minWidth: 168 }
// 검색 입력 폭은 전역에서 같은 기준을 쓰도록 250으로 통일한다.
export const LIST_FILTER_SEARCH_FIELD_SX = { flex: 1, width: 250 }
export const LIST_FILTER_SEARCH_BUTTON_SX = { p: '10px', ml: 1 }
// MUI sx의 숫자 radius는 theme 배율이 곱해지므로 공통 필터 모서리는 px로 고정한다.
const LIST_FILTER_SURFACE_RADIUS = `${adminRadii.control}px`
const LIST_FILTER_CONTROL_RADIUS = '3px'
const LIST_FILTER_ACTION_RADIUS = `${adminRadii.control}px`
const LIST_FILTER_CONTROL_HEIGHT = '36px'
const LIST_FILTER_ACTION_SIZE = '34px'
const LIST_FILTER_SCROLLBAR_COLOR = 'rgba(139, 149, 161, 0.6)'

const listFilterBarSx = {
    display: 'flex',
    width: '100%',
    flexWrap: 'wrap',
    alignItems: 'center',
    justifyContent: 'center',
    columnGap: 0.5,
    rowGap: 0.5,
    mt: 0.5,
    mb: 2,
}

const listFilterFormSx = {
    px: '14px',
    // 박스 보더와 Outlined label이 겹치지 않도록 위쪽 여백을 충분히 둔다.
    pt: '14px',
    pb: '10px',
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'flex-start',
    width: 'auto',
    maxWidth: '100%',
    overflowX: 'auto',
    overflowY: 'hidden',
    alignSelf: 'center',
    borderRadius: LIST_FILTER_SURFACE_RADIUS,
    border: '1px solid',
    borderColor: 'divider',
    backgroundColor: 'background.paper',
    boxShadow: adminShadows.surface,
    scrollbarWidth: 'thin',
    scrollbarColor: `${LIST_FILTER_SCROLLBAR_COLOR} transparent`,
    '&::-webkit-scrollbar': {
        height: 6,
    },
    '&::-webkit-scrollbar-thumb': {
        borderRadius: 999,
        backgroundColor: LIST_FILTER_SCROLLBAR_COLOR,
    },
    '& > .MuiIconButton-root': {
        width: LIST_FILTER_ACTION_SIZE,
        height: LIST_FILTER_ACTION_SIZE,
        flexShrink: 0,
        borderRadius: LIST_FILTER_ACTION_RADIUS,
        color: 'text.secondary',
        backgroundColor: 'background.paper',
        border: '1px solid',
        borderColor: 'divider',
        boxShadow: 'none',
        transition: 'background-color 0.15s ease, color 0.15s ease, border-color 0.15s ease, transform 0.15s ease',
    },
    '& > .MuiIconButton-root:hover': {
        color: 'text.primary',
        backgroundColor: 'background.default',
        borderColor: 'text.secondary',
        transform: 'translateY(-1px)',
    },
    '& > .MuiIconButton-root.Mui-disabled': {
        color: 'text.disabled',
        backgroundColor: 'background.paper',
        borderColor: 'divider',
    },
    '& > .MuiInputBase-root': {
        height: LIST_FILTER_CONTROL_HEIGHT,
        flexShrink: 0,
        display: 'flex',
        alignItems: 'center',
        px: '10px',
        borderRadius: LIST_FILTER_CONTROL_RADIUS,
        border: '1px solid',
        borderColor: 'divider',
        backgroundColor: 'background.default',
        boxShadow: `inset 0 1px 0 ${adminColors.gray50}`,
    },
    '& > .MuiInputBase-root:hover': {
        borderColor: 'text.secondary',
    },
    '& > .MuiInputBase-root:focus-within': {
        borderColor: 'primary.main',
        backgroundColor: 'background.paper',
    },
    '& > .MuiInputBase-root .MuiInputBase-input': {
        padding: 0,
    },
    '& .MuiFormControl-root': {
        flexShrink: 0,
    },
    '& .MuiOutlinedInput-root': {
        height: LIST_FILTER_CONTROL_HEIGHT,
        borderRadius: LIST_FILTER_CONTROL_RADIUS,
        backgroundColor: 'background.paper',
    },
    '& .MuiOutlinedInput-input': {
        paddingTop: '8px',
        paddingBottom: '8px',
    },
    '& .MuiInputAdornment-root .MuiIconButton-root': {
        width: 30,
        height: 30,
    },
    '& .MuiInputAdornment-root .MuiSvgIcon-root': {
        fontSize: 18,
    },
    '& .MuiSelect-select': {
        paddingTop: '8px',
        paddingBottom: '8px',
    },
    '& .MuiOutlinedInput-notchedOutline': {
        borderColor: 'divider',
    },
    '& .MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline': {
        borderColor: 'text.secondary',
    },
    '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': {
        borderColor: 'primary.main',
        borderWidth: 1,
    },
    '& .MuiInputLabel-root': {
        color: 'text.secondary',
        fontWeight: 600,
        fontSize: 13,
    },
    '& .MuiDivider-root': {
        alignSelf: 'stretch',
        // margin 숫자는 theme spacing 배율이 곱해지므로 divider 여백은 px로 고정한다.
        marginTop: '6px',
        marginBottom: '6px',
        marginLeft: '4px',
        marginRight: '4px',
        borderColor: 'divider',
    },
    '& .list-filter-trailing': {
        display: 'inline-flex',
        alignItems: 'center',
        flexShrink: 0,
        marginLeft: '10px',
    },
}

// 목록 필터 바의 정렬과 간격을 공통 레이아웃으로 맞춘다.
export function ListFilterBar({ children, sx, ...props }) {
    return (
        <Box sx={[listFilterBarSx, sx]} {...props}>
            {children}
        </Box>
    )
}

// 긴 필터 조합도 줄 높이와 스크롤 동작이 일정하도록 검색 폼 컨테이너를 고정한다.
export function ListFilterForm({ children, onSubmit, trailing, elevation: _elevation, sx, ...props }) {
    return (
        <Box
            component="form"
            onSubmit={onSubmit}
            sx={[listFilterFormSx, sx]}
            {...props}
        >
            {children}
            {trailing ? <Box className="list-filter-trailing">{trailing}</Box> : null}
        </Box>
    )
}
